const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'SPAN'])

const FONT_SIZE_MAP = {
  1: '10px',
  2: '13px',
  3: '16px',
  4: '18px',
  5: '24px',
  6: '32px',
  7: '48px',
}

const escapeHtml = value => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const HTML_MARKUP_OR_ENTITY_REGEX = /<\/?[a-z][\s\S]*>|&(?:[a-z]+|#\d+|#x[\da-f]+);/i

const containsHtmlMarkupOrEntity = value => HTML_MARKUP_OR_ENTITY_REGEX.test(value)

const sanitizeStyle = styleValue => {
  if (!styleValue) {
    return ''
  }

  const fontSizeMatch = String(styleValue).match(/font-size\s*:\s*([^;]+)/i)
  if (!fontSizeMatch) {
    return ''
  }

  const rawSize = fontSizeMatch[1].trim()
  if (/^\d+(px|rem|em|%)$/.test(rawSize)) {
    return `font-size: ${rawSize};`
  }

  return ''
}

const applyFontSizePresets = root => {
  root.querySelectorAll('font[size]').forEach(node => {
    const size = node.getAttribute('size')
    const span = document.createElement('span')
    const mappedSize = FONT_SIZE_MAP[size] || FONT_SIZE_MAP['3']
    span.setAttribute('style', `font-size: ${mappedSize};`)
    span.innerHTML = node.innerHTML
    node.replaceWith(span)
  })
}

const sanitizeNode = (node, options = {}) => {
  const { preserveNbsp = false } = options

  if (!node) {
    return
  }

  const children = Array.from(node.childNodes)
  children.forEach(child => {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove()

      return
    }

    if (child.nodeType === Node.TEXT_NODE) {
      child.nodeValue = preserveNbsp
        ? String(child.nodeValue || '')
        : String(child.nodeValue || '')
          .replace(/\u00a0/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/&amp;nbsp;/gi, ' ')

      return
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove()

      return
    }

    const tagName = child.tagName.toUpperCase()
    if (!ALLOWED_TAGS.has(tagName)) {
      if (tagName === 'UL' || tagName === 'OL') {
        const fragment = document.createDocumentFragment()
        Array.from(child.children)
          .filter(listItem => listItem.tagName && listItem.tagName.toUpperCase() === 'LI')
          .forEach(listItem => {
            const block = document.createElement('div')
            block.innerHTML = listItem.innerHTML
            sanitizeNode(block, options)
            fragment.appendChild(block)
          })
        child.replaceWith(fragment)

        return
      }

      if (tagName === 'LI') {
        const block = document.createElement('div')
        block.innerHTML = child.innerHTML
        sanitizeNode(block, options)
        child.replaceWith(block)

        return
      }

      const text = document.createTextNode(child.textContent || '')
      child.replaceWith(text)

      return
    }

    Array.from(child.attributes).forEach(attr => {
      const attrName = attr.name.toLowerCase()
      if (attrName.startsWith('on')) {
        child.removeAttribute(attr.name)

        return
      }

      if (attrName === 'style') {
        const sanitized = sanitizeStyle(attr.value)
        if (sanitized) {
          child.setAttribute('style', sanitized)
        } else {
          child.removeAttribute('style')
        }

        return
      }

      if (attrName === 'class') {
        const classes = String(attr.value || '')
          .split(/\s+/)
          .filter(Boolean)
          .filter(className => className !== 'typing-marker')

        if (classes.length) {
          child.setAttribute('class', classes.join(' '))
        } else {
          child.removeAttribute('class')
        }

        return
      }

      if (attrName !== 'class') {
        child.removeAttribute(attr.name)
      }
    })

    sanitizeNode(child, options)
  })
}

const getPlainText = value => {
  if (typeof document === 'undefined') {
    return String(value || '')
  }

  const raw = value == null ? '' : String(value)
  const containsHtml = containsHtmlMarkupOrEntity(raw)
  const template = document.createElement('template')

  template.innerHTML = containsHtml ? raw : escapeHtml(raw).replace(/\n/g, '<br>')
  applyFontSizePresets(template.content)
  sanitizeNode(template.content)

  return template.content.textContent || ''
}

export const sanitizeRichText = value => {
  if (typeof document === 'undefined') {
    return value || ''
  }

  const raw = value == null ? '' : String(value)
  const containsHtml = containsHtmlMarkupOrEntity(raw)
  const template = document.createElement('template')

  template.innerHTML = containsHtml ? raw : escapeHtml(raw).replace(/\n/g, '<br>')
  applyFontSizePresets(template.content)
  sanitizeNode(template.content, { preserveNbsp: true })

  return template.innerHTML
}

export const normalizeRichTextValue = value => {
  const normalized = sanitizeRichText(value)

  return isRichTextEmpty(normalized) ? '' : normalized
}

export const isRichTextEmpty = value => !getPlainText(value).trim().length
